// @greenlight-spec: 28-it-dashboard-ui.md
import * as React from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { EmptyState } from '@/components/greenlight/EmptyState';
import { FirstAppPrompt, SetUpIntegrationsButton } from '@/components/greenlight/FirstAppPrompt';
import {
  AGENT_SETUP_PATH,
  MARKETPLACE_CREATE_PATH,
  OrgAdminContacts,
  pluginSetupState,
} from '@/components/settings/pluginSetup';
import { Button } from '@/components/ui/button';
import type { OrgRole, OrgSettings } from '@greenlight/shared';

export interface AppsEmptyStateProps {
  role: OrgRole | undefined;
  /** Undefined when settings could not be read; the builder path still leads to Agent setup. */
  agentSetup: OrgSettings['agent_setup'] | undefined;
}

async function copyAgentSetupLink(): Promise<void> {
  try {
    await navigator.clipboard.writeText(new URL(AGENT_SETUP_PATH, window.location.origin).href);
    toast.success('Agent setup link copied.');
  } catch {
    toast.error("Couldn't copy the link.");
  }
}

export function AppsEmptyState({ role, agentSetup }: AppsEmptyStateProps): React.JSX.Element {
  const hasMarketplace = agentSetup ? agentSetup.marketplace_repositories.length > 0 : true;
  const state = pluginSetupState(role, hasMarketplace);

  switch (state) {
    case 'admin-rollout':
      return (
        <EmptyState
          title="Roll out the Greenlight plugin to your org"
          description="Builders' coding agents install the Greenlight plugin from a Greenlight agent plugin repo your org owns. Create it, then share the install steps with your builders; every app they build shows up here."
          action={
            <Button asChild size="sm">
              <Link to={MARKETPLACE_CREATE_PATH}>Create the agent plugin repo</Link>
            </Button>
          }
        />
      );
    case 'admin-ready':
      return (
        <EmptyState
          title="Greenlight is fully configured"
          action={
            <div className="flex flex-col items-center gap-4">
              <FirstAppPrompt className="text-center" />
              <div className="flex flex-wrap justify-center gap-2">
                <Button asChild size="sm">
                  <Link to={AGENT_SETUP_PATH}>Install the plugin</Link>
                </Button>
                <Button size="sm" variant="outline" onClick={() => void copyAgentSetupLink()}>
                  Copy setup link for builders
                </Button>
                <SetUpIntegrationsButton />
              </div>
            </div>
          }
        />
      );
    case 'builder-waiting':
      return (
        <EmptyState
          title="Your admin hasn't set up the Greenlight plugin yet"
          description="Your coding agent builds and deploys apps through the Greenlight plugin, and an org admin sets it up once for everyone."
          action={
            <p className="text-sm text-muted-foreground" data-testid="apps-empty-admins">
              <OrgAdminContacts admins={agentSetup?.org_admins} />
            </p>
          }
        />
      );
    case 'builder-ready':
      return (
        <EmptyState
          title="Build your first app"
          action={
            <div className="flex flex-col items-center gap-4">
              <FirstAppPrompt className="text-center" />
              <Button asChild size="sm">
                <Link to={AGENT_SETUP_PATH}>Install the plugin</Link>
              </Button>
            </div>
          }
        />
      );
  }
}
